import { Link } from '@tanstack/react-router';
import type { TeamRequestEntry } from '@hygge/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useDecideAbsenceRequest } from '@/hooks/use-dashboard';
import { extractErrorMessage } from '@/lib/errors';
import { ExpandableList } from '../expandable-list';
import { formatRange } from '../widget-format';

type Decide = ReturnType<typeof useDecideAbsenceRequest>;

function RequestRow({
  r,
  formatName,
  decide,
}: {
  r: TeamRequestEntry;
  formatName: (e: { firstName: string; lastName: string; preferredName?: string | null }) => string;
  decide: Decide;
}) {
  const initials =
    `${r.employee.firstName?.[0] ?? ''}${r.employee.lastName?.[0] ?? ''}`.toUpperCase();
  // One shared decision mutation backs every row, so guard against a cross-row double-submit:
  // while any Approve/Reject is in flight, disable every row's buttons until it resolves.
  const decisionInFlight = decide.isPending;
  const days = `${r.totalDays} day${r.totalDays === 1 ? '' : 's'}`;

  return (
    <div className="dashboard-row flex items-center gap-2 border-b border-border py-2.5 last:border-0">
      <Link
        to="/employees/$id"
        params={{ id: r.employee.id }}
        search={{ tab: 'absences' }}
        className="group flex min-w-0 flex-1 items-center gap-3"
      >
        <Avatar className="h-8 w-8">
          <AvatarImage src={r.employee.avatarUrl ?? undefined} />
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium group-hover:underline">
            {formatName(r.employee)}
          </div>
          <div className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
            {r.absenceTypeColor && (
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: r.absenceTypeColor }}
              />
            )}
            <span className="truncate">
              {r.absenceTypeName} · {formatRange(r.startDate, r.endDate)} · {days}
            </span>
          </div>
        </div>
      </Link>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          disabled={decisionInFlight}
          onClick={() =>
            decide.mutate({
              requestId: r.requestId,
              decision: 'approve',
              employeeId: r.employee.id,
            })
          }
          className="cursor-pointer rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-emerald-500 hover:text-emerald-600 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:text-emerald-400"
        >
          Approve
        </button>
        <button
          type="button"
          disabled={decisionInFlight}
          onClick={() =>
            decide.mutate({
              requestId: r.requestId,
              decision: 'reject',
              employeeId: r.employee.id,
            })
          }
          className="cursor-pointer rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-red-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:text-red-400"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

interface TeamRequestsListProps {
  data: TeamRequestEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Presentational body for the team-requests widget — the caller's direct
 * reports' pending absence requests with inline Approve/Reject. The name links to the
 * person's profile Absences tab.
 */
export function TeamRequestsList({ data, isLoading, isError }: TeamRequestsListProps) {
  const formatName = useEmployeeNameFormatter();
  const decide = useDecideAbsenceRequest();
  const rows = data ?? [];

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-28" />
            </div>
            <Skeleton className="h-7 w-14" />
          </div>
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">
        Couldn&apos;t load pending requests.
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">Nothing awaiting your approval.</p>
    );
  }

  return (
    <>
      {decide.isError && (
        <p className="mb-2 text-xs text-destructive">
          {extractErrorMessage(decide.error, "Couldn't apply your decision — please try again.")}
        </p>
      )}
      <ExpandableList
        items={rows}
        getKey={(r) => r.requestId}
        renderItem={(r: TeamRequestEntry) => (
          <RequestRow r={r} formatName={formatName} decide={decide} />
        )}
      />
    </>
  );
}
